import { PermissionGate } from "@/components/common/PermissionGate"
import { Button } from "@/components/ui/button"
import { Switch } from "@/components/ui/switch"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PERMISSIONS } from "@/constants/permissions"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconLoader2 } from "@tabler/icons-react"
import { Check, DatabaseZap, Info, TriangleAlert } from "lucide-react"
import { useMemo } from "react"
import type {
  SkippedComplianceColumn,
  SupplierNumberTwin,
  SupplierNumberTwinsMode,
} from "../../api/importsApi"
import { type EntityGroup, type EntityGroupType } from "../../api/ingestionApi"
import { EntityScoreboard } from "./EntityScoreboard"
import { RADIAL_REVIEW_GLOW } from "./masterDataStyles"
export { ENTITY_ICONS } from "./EntityIcons"

const BTN_PRIMARY = "bg-yellow1 text-black hover:bg-yellow2"

interface ImportOptionSwitchProps {
  id: string
  checked: boolean
  onCheckedChange: (value: boolean) => void
  title: string
  hint: string
  tip: string
}

/** One import option: a switch, a short title with the long explanation behind
 *  an info tooltip, and one line of subtext. A plain div, not a label wrapping
 *  everything: inside a label, clicking the info icon would toggle the switch. */
function ImportOptionSwitch({
  id,
  checked,
  onCheckedChange,
  title,
  hint,
  tip,
}: ImportOptionSwitchProps) {
  return (
    <div className="flex w-full items-start gap-3 rounded-md border border-grey4 px-4 py-2.5 text-left">
      <Switch
        id={id}
        checked={checked}
        onCheckedChange={onCheckedChange}
        className="mt-0.5"
      />
      <span className="flex-1">
        <span className="flex items-center gap-1.5">
          <label
            htmlFor={id}
            className="cursor-pointer text-sm text-foreground"
          >
            {title}
          </label>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={tip}
                  className="shrink-0 text-grey5 transition-colors hover:text-white"
                >
                  <Info size={13} />
                </button>
              </TooltipTrigger>
              <TooltipContent className="max-w-[320px] text-xs">
                {tip}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <span className="block text-xs text-muted-foreground">{hint}</span>
      </span>
    </div>
  )
}

// ─── Review & commit step ─────────────────────────────────────────────────────

interface MasterDataReviewStepProps {
  groups: EntityGroup[]
  activeGroup: EntityGroup | null
  selectedGroupType: string
  onGroupSelect: (type: string) => void
  onBackToMapping: () => void
  onCommit: () => void
  canCommit: boolean
  committing?: boolean
  totalToWrite: number
  fileCount: number
  /** Q19 sell-through option — rendered only when the parent passes a setter
   *  (conformity v2 flag on). */
  soldAsIs?: boolean
  onSoldAsIsChange?: (value: boolean) => void
  /** TUR-1539 §3.6: keep-empty vs overwrite on reimport, rendered only when
   *  the parent passes a setter (this batch maps articles.product_group). */
  productGroupReimportMode?: "keep_empty" | "overwrite"
  onProductGroupReimportModeChange?: (value: "keep_empty" | "overwrite") => void
  /** The org's configured label for articles.product_group
   *  (templateSettings.productGroupLabel), used in the reimport option copy.
   *  Falls back to the default "Product group" translation when omitted. */
  productGroupLabel?: string
  /** Compliance columns the commit will skip because their regulation is not
   *  supported (reg-DB import resolution on). Notice hidden when empty. */
  skippedComplianceColumns?: SkippedComplianceColumn[]
  /** Incoming supplier numbers that only an ERP suffix separates from existing
   *  suppliers. Box hidden when empty. */
  supplierNumberTwins?: SupplierNumberTwin[]
  supplierNumberTwinsMode?: SupplierNumberTwinsMode
  onSupplierNumberTwinsModeChange?: (value: SupplierNumberTwinsMode) => void
}

// The first few matches are enough to recognise the pattern; the rest collapse.
const MAX_TWINS_SHOWN = 5

/** "ELV Status (ELV)" / "Sonstige Status (no regulation recognised)". Any
 *  reason other than "unrecognised" names its framework, so a reason this
 *  build does not know still renders. */
function skippedColumnLabel(
  col: SkippedComplianceColumn,
  translate: ReturnType<typeof useTranslation>["translate"],
): string {
  if (col.reason === "unrecognised" || !col.framework) {
    return translate("MC_MD_REVIEW_SKIPPED_COLUMN_UNRECOGNISED", {
      column: col.column,
    })
  }
  return translate("MC_MD_REVIEW_SKIPPED_COLUMN_UNSUPPORTED", {
    column: col.column,
    framework: col.framework,
  })
}

export function MasterDataReviewStep({
  groups,
  onBackToMapping,
  onCommit,
  canCommit,
  committing = false,
  totalToWrite,
  fileCount,
  soldAsIs = false,
  onSoldAsIsChange,
  productGroupReimportMode = "keep_empty",
  onProductGroupReimportModeChange,
  productGroupLabel,
  skippedComplianceColumns = [],
  supplierNumberTwins = [],
  supplierNumberTwinsMode = "link",
  onSupplierNumberTwinsModeChange,
}: MasterDataReviewStepProps) {
  const { translate } = useTranslation()
  const resolvedProductGroupLabel =
    productGroupLabel || translate("MC_MD_FIELD_PRODUCT_GROUP")

  const counts = useMemo(() => {
    const record = {} as Record<EntityGroupType, number>
    for (const group of groups) record[group.type] = group.count
    return record
  }, [groups])

  return (
    <div className="relative flex h-full min-h-0 flex-col overflow-hidden">
      <div
        className="pointer-events-none absolute inset-0"
        style={RADIAL_REVIEW_GLOW}
      />

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-6 py-4">
        <div className="flex w-full max-w-2xl flex-col items-center gap-8 text-center">
          {/* Icon */}
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
            <DatabaseZap className="h-5 w-5" />
          </div>

          {committing ? (
            <div
              className="flex flex-col items-center gap-3 py-10 text-center"
              data-testid="review-committing"
              role="status"
              aria-live="polite"
            >
              <IconLoader2
                className="h-5 w-5 animate-spin text-primary"
                aria-hidden
              />
              <p className="text-lg font-medium text-foreground">
                {translate("MC_MD_REVIEW_COMMITTING_TITLE")}
              </p>
              <p className="max-w-md text-sm text-muted-foreground">
                {translate("MC_MD_REVIEW_COMMITTING_HINT")}
              </p>
            </div>
          ) : (
            <>
              <div className="space-y-2">
                <p className="text-lg font-medium text-foreground">
                  {translate("MC_MD_REVIEW_COMMIT_TITLE")}
                </p>
                <p className="text-sm text-muted-foreground">
                  {translate(
                    fileCount === 1
                      ? "MC_MD_REVIEW_COMMIT_BODY_ONE"
                      : "MC_MD_REVIEW_COMMIT_BODY_MANY",
                    { count: fileCount, total: totalToWrite },
                  )}
                </p>
              </div>

              <EntityScoreboard counts={counts} />

              {onSoldAsIsChange && (
                <ImportOptionSwitch
                  id="import-sold-as-is"
                  checked={soldAsIs}
                  onCheckedChange={onSoldAsIsChange}
                  title={translate("MC_MD_IMPORT_SOLD_AS_IS")}
                  hint={translate("MC_MD_IMPORT_SOLD_AS_IS_HINT")}
                  tip={translate("MC_MD_IMPORT_SOLD_AS_IS_TIP")}
                />
              )}

              {onProductGroupReimportModeChange && (
                <ImportOptionSwitch
                  id="import-product-group-reimport"
                  checked={productGroupReimportMode === "overwrite"}
                  onCheckedChange={(value) =>
                    onProductGroupReimportModeChange(
                      value ? "overwrite" : "keep_empty",
                    )
                  }
                  title={translate("MC_MD_MAP_PRODUCT_GROUP_REIMPORT_LABEL", {
                    label: resolvedProductGroupLabel,
                  })}
                  hint={translate("MC_MD_MAP_PRODUCT_GROUP_REIMPORT_HINT", {
                    label: resolvedProductGroupLabel,
                  })}
                  tip={translate("MC_MD_MAP_PRODUCT_GROUP_REIMPORT_TIP", {
                    label: resolvedProductGroupLabel,
                  })}
                />
              )}

              {supplierNumberTwins.length > 0 &&
                onSupplierNumberTwinsModeChange && (
                  <SupplierNumberTwins
                    twins={supplierNumberTwins}
                    mode={supplierNumberTwinsMode}
                    onModeChange={onSupplierNumberTwinsModeChange}
                  />
                )}

              {skippedComplianceColumns.length > 0 && (
                <div
                  className="flex items-start gap-2 rounded-md border border-grey4 px-4 py-2.5 text-left"
                  data-testid="review-skipped-compliance-columns"
                >
                  <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                  <p className="text-sm text-muted-foreground">
                    {translate("MC_MD_REVIEW_SKIPPED_COLUMNS", {
                      count: skippedComplianceColumns.length,
                      columns: skippedComplianceColumns
                        .map((col) => skippedColumnLabel(col, translate))
                        .join(
                          translate("MC_MD_REVIEW_SKIPPED_COLUMNS_SEPARATOR"),
                        ),
                    })}
                  </p>
                </div>
              )}

              <div className="flex items-center gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-2.5">
                <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
                <p className="text-sm text-warning">
                  {translate("MC_MD_REVIEW_COMMIT_NOTE")}
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      <div className="relative flex items-center justify-end gap-3 px-6 py-4">
        <Button
          variant="outline"
          onClick={onBackToMapping}
          disabled={committing}
        >
          {translate("MC_MD_MAP_BACK_TO_MAPPING")}
        </Button>
        <PermissionGate permission={PERMISSIONS.MC_IMPORTS_COMMIT}>
          <Button
            data-agent-target="import.commit-button"
            className={BTN_PRIMARY}
            disabled={!canCommit || committing}
            onClick={onCommit}
          >
            {committing ? (
              <IconLoader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Check className="mr-2 h-4 w-4" />
            )}
            {translate("MC_MD_MAP_COMMIT_CHANGES")}
          </Button>
        </PermissionGate>
      </div>
    </div>
  )
}

/** The incoming supplier numbers that look like existing suppliers ("0011335 000" vs
 *  "0011335") and the choice to use the existing supplier or create a new one. A
 *  number with several matches is never linked; it says so. */
function SupplierNumberTwins({
  twins,
  mode,
  onModeChange,
}: {
  twins: SupplierNumberTwin[]
  mode: SupplierNumberTwinsMode
  onModeChange: (value: SupplierNumberTwinsMode) => void
}) {
  const { translate } = useTranslation()
  const shown = twins.slice(0, MAX_TWINS_SHOWN)
  return (
    <div
      className="flex w-full flex-col gap-2 rounded-md border border-grey4 px-4 py-2.5 text-left"
      data-testid="review-supplier-number-twins"
    >
      <p className="text-sm text-foreground">
        {translate("MC_MD_REVIEW_SUPPLIER_TWINS_TITLE", {
          count: twins.length,
        })}
      </p>
      <ul className="space-y-0.5 text-xs text-muted-foreground">
        {shown.map((twin) => (
          <li key={twin.incoming}>
            {twin.existing.length === 1
              ? translate("MC_MD_REVIEW_SUPPLIER_TWIN_MATCH", {
                  incoming: twin.incoming,
                  existing: twin.existing[0].supplierNumber,
                  name: twin.existing[0].name ?? "",
                })
              : translate("MC_MD_REVIEW_SUPPLIER_TWIN_AMBIGUOUS", {
                  incoming: twin.incoming,
                  count: twin.existing.length,
                })}
          </li>
        ))}
        {twins.length > shown.length && (
          <li>
            {translate("MC_MD_REVIEW_SUPPLIER_TWINS_MORE", {
              count: twins.length - shown.length,
            })}
          </li>
        )}
      </ul>
      <ImportOptionSwitch
        id="import-supplier-number-twins"
        checked={mode === "link"}
        onCheckedChange={(value) => onModeChange(value ? "link" : "create")}
        title={translate("MC_MD_REVIEW_SUPPLIER_TWINS_LINK")}
        hint={translate("MC_MD_REVIEW_SUPPLIER_TWINS_LINK_HINT")}
        tip={translate("MC_MD_REVIEW_SUPPLIER_TWINS_LINK_TIP")}
      />
    </div>
  )
}
